import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Globe, EyeOff, ExternalLink, ClipboardCopy } from 'lucide-react'
import { api } from '../lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'

// Publish-to-careers-site panel, rendered under the Details tab on the Job
// page. Self-contained: receives the job payload (which carries the public_*
// fields thanks to JobSerializer `fields='__all__'`) and a refetch callback.

type PublishJob = {
  id: number
  status: string
  slug: string
  is_published: boolean
  published_at: string | null
  expires_at: string | null
  team_name: string
  public_description: string
  public_responsibilities: string
  public_requirements: string
  benefits: string
  seo_title: string
  seo_description: string
  // Internal fields — source for the "copy from internal" prefill. NEVER
  // rendered publicly; the recruiter redacts after copying.
  description: string
  responsibilities: string
  requirements: string
}

// Internal fields are plain text; public fields are HTML. Escape, then
// paragraph on blank lines and <br/> on single newlines.
function textToHtml(t: string): string {
  const esc = (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return esc.trim()
    ? esc.trim().split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('\n')
    : ''
}

export default function JobPublishPanel({ job, onChanged }: { job: PublishJob; onChanged: () => void }) {
  const [form, setForm] = useState({
    team_name: job.team_name || '',
    public_description: job.public_description || '',
    public_responsibilities: job.public_responsibilities || '',
    public_requirements: job.public_requirements || '',
    benefits: job.benefits || '',
    seo_title: job.seo_title || '',
    seo_description: job.seo_description || '',
    expires_at: job.expires_at || '',
  })
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value })

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...form }
      if (!payload.expires_at) payload.expires_at = null
      await api.patch(`/ats/jobs/${job.id}/`, payload)
    },
    onSuccess: () => { toast.success('Public content saved'); onChanged() },
    onError: () => toast.error('Save failed'),
  })

  const toggle = useMutation({
    mutationFn: async () => {
      await api.post(`/ats/jobs/${job.id}/${job.is_published ? 'unpublish' : 'publish'}/`)
    },
    onSuccess: () => {
      toast.success(job.is_published ? 'Job removed from careers site' : 'Job published to careers site')
      onChanged()
    },
    onError: (e: any) => {
      const d = e?.response?.data
      toast.error(d?.public_description?.[0] || d?.detail || 'Action failed')
    },
  })

  const publicUrl = `/careers/jobs/${job.slug}`

  const hasPublicContent = !!(form.public_description || form.public_responsibilities || form.public_requirements)
  const copyFromInternal = () => {
    if (hasPublicContent && !window.confirm(
      'Overwrite the current public description / responsibilities / requirements with the internal fields?'
    )) return
    setForm({
      ...form,
      public_description: textToHtml(job.description),
      public_responsibilities: textToHtml(job.responsibilities),
      public_requirements: textToHtml(job.requirements),
    })
    toast.info('Copied from internal fields — redact anything confidential before saving.')
  }

  return (
    <div className="rounded-lg border">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold">Public careers page</h3>
          {job.is_published ? (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Published</Badge>
          ) : (
            <Badge variant="secondary">Not published</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {job.is_published && (
            <a href={publicUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground hover:underline">
              View <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <Button size="sm" variant={job.is_published ? 'outline' : 'default'}
            disabled={toggle.isPending || (job.status !== 'OPEN' && !job.is_published)}
            onClick={() => toggle.mutate()}>
            {job.is_published ? (<><EyeOff className="mr-1 h-3.5 w-3.5" />Unpublish</>) : 'Publish'}
          </Button>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {job.status !== 'OPEN' && !job.is_published && (
          <p className="text-sm text-muted-foreground">Job must be in OPEN status to publish.</p>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="pp-team">Team name (shown on listing)</Label>
            <Input id="pp-team" value={form.team_name} onChange={set('team_name')} />
          </div>
          <div>
            <Label htmlFor="pp-expires">Listing expires (auto-pulled after)</Label>
            <Input id="pp-expires" type="date" value={form.expires_at} onChange={set('expires_at')} />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Candidate-facing content</span>
          <Button type="button" size="sm" variant="ghost" onClick={copyFromInternal}>
            <ClipboardCopy className="mr-1 h-3.5 w-3.5" />Copy from internal fields
          </Button>
        </div>
        <PField id="pp-desc" label="Public description (HTML) — required to publish"
          value={form.public_description} onChange={set('public_description')} rows={6} />
        <PField id="pp-resp" label="Responsibilities (HTML)"
          value={form.public_responsibilities} onChange={set('public_responsibilities')} rows={4} />
        <PField id="pp-req" label="Requirements (HTML)"
          value={form.public_requirements} onChange={set('public_requirements')} rows={4} />
        <PField id="pp-benefits" label="Benefits (plain text)"
          value={form.benefits} onChange={set('benefits')} rows={3} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="pp-seot">SEO title</Label>
            <Input id="pp-seot" value={form.seo_title} onChange={set('seo_title')} />
          </div>
          <div>
            <Label htmlFor="pp-seod">SEO description</Label>
            <Input id="pp-seod" value={form.seo_description} onChange={set('seo_description')} />
          </div>
        </div>
        <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save public content'}
        </Button>
      </div>
    </div>
  )
}

function PField({ id, label, value, rows, onChange }: {
  id: string; label: string; value: string; rows: number
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <textarea id={id} rows={rows} value={value} onChange={onChange}
        className="mt-1 w-full rounded-md border bg-background px-3 py-2 font-mono text-xs" />
    </div>
  )
}
